/**CORE LIBRARIES IMPORT */
import React from "react";
import { motion } from "framer-motion";

/**COMPONENTS IMPORT */
import SectionHeading from "./SectionHeading";
import PhotoPlaceholder from "./PhotoPlaceholder";
import Icon from "./Icon";

/**
 * Event entertainment: confirmed highlights + performer line-up.
 * Performer cards reuse the template's DJ card (img-wrapper + hover-box).
 * With no confirmed performers, it says so; no names or photos are invented.
 */
const EventEntertainment = ({ entertainment, sampleNotice = false }) => {
  if (!entertainment) return null;
  const performers = [...(entertainment.performers || [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );

  return (
    <section className="menu-card entertainment" aria-labelledby="entertainment-heading">
      <SectionHeading id="entertainment-heading" title="Live Music & Entertainment" />

      {entertainment.highlights?.length > 0 && (
        <ul className="about-features entertainment-highlights">
          {entertainment.highlights.map((h) => (
            <li key={h.title} className="feature">
              <div className="icon-wrapper">
                <div className="icon-circle">
                  <Icon name="music" size={40} />
                </div>
              </div>
              <span>{h.title}</span>
              <p className="section-text">{h.text}</p>
            </li>
          ))}
        </ul>
      )}

      <h3 className="menu-course">Performers</h3>
      {sampleNotice && (
        <p className="dev-banner" role="note">Development preview: sample performers for layout only.</p>
      )}

      {performers.length === 0 ? (
        <p className="section-text performers-tba">Performers to be announced.</p>
      ) : (
        <motion.ul
          className="dj-grid performer-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.15 } } }}
        >
          {performers.map((p) => (
            <motion.li
              key={p.id}
              className="dj-card performer-card"
              variants={{ hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
              whileHover="hover"
            >
              <motion.div className="img-wrapper" variants={{ hover: { scale: 1.05 } }} transition={{ duration: 0.3 }}>
                {p.photo?.src ? (
                  <img src={p.photo.src} alt={p.photo.alt || p.name} loading="lazy" decoding="async" />
                ) : (
                  <PhotoPlaceholder ratio="3 / 4" />
                )}
              </motion.div>
              <motion.h4 className="section-text" variants={{ hover: { opacity: 0 } }} transition={{ duration: 0.3 }}>
                {p.name}
              </motion.h4>
              <motion.div
                className="hover-box"
                initial={{ y: "10%" }}
                variants={{ hover: { y: 0 } }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              >
                <p>{p.name}</p>
                {p.time && <p>{p.time}</p>}
                {p.description && <p>{p.description}</p>}
              </motion.div>
            </motion.li>
          ))}
        </motion.ul>
      )}
    </section>
  );
};

export default EventEntertainment;
